import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { cn } from '@workspace/ui/lib/utils'
import { useEffect, useState } from 'react'

/**
 * The wait while the sequence is written.
 *
 * A bare spinner was wrong here: a first generation writes every version of all
 * three messages for both channels, measured at ~50s locally, and silence for
 * that long reads as broken.
 *
 * The copy gives a time expectation and nothing else — the founder does not need
 * our experiment vocabulary while they wait. It escalates rather than counting
 * down, because we cannot honestly predict the finish: model latency varies and
 * a validator retry doubles it, so any number would be a promise we might break.
 */
export function MessagesPreparing({ hidden }: { hidden?: boolean }) {
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    const id = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => clearInterval(id)
  }, [])

  // 45s is past the median first generation, so the second line only appears
  // when the wait is genuinely longer than usual.
  const slow = elapsed >= 45

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 py-16 text-center',
        hidden && 'hidden'
      )}
    >
      <LoadingSpinner size="sm" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">Preparing your messages…</p>
        <p className="max-w-[380px] text-[12px] leading-relaxed text-muted-foreground">
          {slow
            ? 'This is taking longer than usual — up to a couple of minutes. You can close this and come back, nothing is lost.'
            : 'This usually takes under a minute.'}
        </p>
      </div>
    </div>
  )
}
